import i18n from 'infrastructure/i18n';
import { useState } from 'react';
import BasePagination from 'components/atoms/base-pagination';
import { useOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';
import useUser from 'utils/useUser';
import classNames from 'classnames';

import s from './styles.module.scss';
import ReadingsColumn from './readings';
import PatientCell from './patient';
import AssignCell from './assign';
import ExpandContainer from '../expand-container';
import Averages from '../averages';
import PatientInfo from '../patient-info';
import ReadingTable from '../reading-table';
import { columnsWidth, PAGE_SIZE } from '../../constants';

import type { RpmOverviewPatient } from 'infrastructure/hooks/rpm/overview-dashboard/use-rpm-overview-dashboard';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

export const useRpmOverviewTableColumns = () => {
  const [expandIndexes, setExpandIndexes] = useState<number[]>([]);
  const {
    filter: { endTime, startTime },
  } = useOverviewDashboardFilter();
  const {
    isMobileDevice,
    isCriticalOrAtRiskAlertingRole,
    isCriticalRole,
    isAtRiskRole,
    isAgencyDoctor,
  } = useUser();

  const hideShowAssignCell = isCriticalOrAtRiskAlertingRole || !isAgencyDoctor;

  let readingCellWidth = columnsWidth.readingType;
  if (isCriticalRole) {
    readingCellWidth = columnsWidth.readingType / 3;
  } else if (isAtRiskRole) {
    readingCellWidth = (columnsWidth.readingType / 3) * 2;
  }

  const columns: IBaseTableColumns<RpmOverviewPatient> = [
    {
      label: i18n.t('labels.patient'),
      key: 'patient',
      onCell: () => ({
        colSpan: 3,
      }),
      render: (_, record, idx) => {
        const isExpanded = expandIndexes.includes(idx);

        const onExpand = () => {
          if (isExpanded) {
            setExpandIndexes(expandIndexes.filter((el) => el !== idx));
          } else {
            setExpandIndexes([...expandIndexes, idx]);
          }
        };

        return (
          <div className={s['column-wrapper']}>
            <div className={s['row-wrapper']}>
              <PatientCell
                guid={record.guid}
                dob={record.dob}
                name={record.name}
                language={record.language}
                provider={record.provider}
                onExpand={onExpand}
                expand={isExpanded}
                croStatus={record.croStatus}
                interactionStatus={record.interactionStatus}
                doNotCallRestrictionType={record.doNotCallRestrictionType}
                doNotCallStartTime={record.doNotCallStartTime}
                doNotCallEndTime={record.doNotCallEndTime}
                clinicDoNotCallRestrictionType={
                  record.clinicDoNotCallRestrictionType
                }
                clinicDoNotCallStartTime={record.clinicDoNotCallStartTime}
                clinicDoNotCallEndTime={record.clinicDoNotCallEndTime}
                timezone={record.timezone}
                actionedByAgencyUser={record.actionedByAgencyUser}
                state={record.state}
              />
              <ReadingsColumn
                critical={record.readings.critical}
                atRisk={record.readings.atRisk}
                normal={record.readings.normal}
                disableRiskAndNormal={record?.overTime}
              />
              {!hideShowAssignCell && (
                <AssignCell
                  patientGuid={record.guid}
                  assignee={record.assignee}
                />
              )}
            </div>
            {isExpanded && (
              <ExpandContainer
                patientGuid={record.guid}
                endTime={endTime}
                startTime={startTime}
                average={
                  !isCriticalOrAtRiskAlertingRole && (
                    <Averages patientGuid={record.guid} endTime={endTime} />
                  )
                }
                info={
                  !isCriticalOrAtRiskAlertingRole && (
                    <PatientInfo patientGuid={record.guid} />
                  )
                }
                table={({ page, activity, loading }) => (
                  <ReadingTable
                    activity={activity}
                    isLoading={loading}
                    page={page}
                    name={record.name}
                  />
                )}
                pagination={({ page, setPage, activity }) => (
                  <BasePagination
                    page={page}
                    total={activity?.count ?? 0}
                    align="center"
                    onChange={(newPage) => {
                      setPage(newPage);
                    }}
                    pageSize={PAGE_SIZE}
                    pageSizeOptions={[]}
                    hide={false}
                    showSizeChanger={false}
                    className={s.pagination}
                  />
                )}
              />
            )}
          </div>
        );
      },
    },
    {
      label: i18n.t('labels.reading'),
      key: 'readingType',
      width: `${readingCellWidth}px`,
      onCell: () => ({
        colSpan: 0,
      }),
    },
  ];

  if (!hideShowAssignCell) {
    columns.push({
      label: i18n.t('labels.assign'),
      key: 'comments',
      width: `${columnsWidth.assign}px`,
      onCell: () => ({
        colSpan: 0,
      }),
    });
  }

  const mobileColumns: IBaseTableColumns<RpmOverviewPatient> = [
    {
      label: i18n.t('labels.patient'),
      key: 'patient',
      render: (_, record, idx) => {
        const isExpanded = expandIndexes.includes(idx);

        const onExpand = () => {
          if (isExpanded) {
            setExpandIndexes(expandIndexes.filter((el) => el !== idx));
          } else {
            setExpandIndexes([...expandIndexes, idx]);
          }
        };
        return (
          <div className={s['mobile-column-wrapper']}>
            <div className={s['mobile-row-wrapper']}>
              <PatientCell
                guid={record.guid}
                dob={record.dob}
                name={record.name}
                language={record.language}
                provider={record.provider}
                onExpand={onExpand}
                expand={isExpanded}
              />
              <div
                className={classNames(s['mobile-readings-wrapper'], {
                  [s['two-rows']]: isCriticalOrAtRiskAlertingRole,
                })}
              >
                <ReadingsColumn
                  critical={record.readings.critical}
                  atRisk={record.readings.atRisk}
                  normal={record.readings.normal}
                  disableRiskAndNormal={record?.overTime}
                />
                {!hideShowAssignCell && (
                  <AssignCell
                    patientGuid={record.guid}
                    assignee={record.assignee}
                  />
                )}
              </div>
            </div>
            {isExpanded && (
              <ExpandContainer
                patientGuid={record.guid}
                endTime={endTime}
                startTime={startTime}
                average={
                  !isCriticalOrAtRiskAlertingRole && (
                    <Averages patientGuid={record.guid} endTime={endTime} />
                  )
                }
                info={
                  !isCriticalOrAtRiskAlertingRole && (
                    <PatientInfo patientGuid={record.guid} />
                  )
                }
                table={({ page, activity, loading }) => (
                  <ReadingTable
                    activity={activity}
                    isLoading={loading}
                    page={page}
                    name={record.name}
                  />
                )}
                pagination={({ page, setPage, activity }) => (
                  <BasePagination
                    page={page}
                    total={activity?.count ?? 0}
                    align="center"
                    onChange={(newPage) => {
                      setPage(newPage);
                    }}
                    pageSize={PAGE_SIZE}
                    pageSizeOptions={[]}
                    hide={false}
                    showSizeChanger={false}
                    className={s.pagination}
                  />
                )}
              />
            )}
          </div>
        );
      },
    },
  ];

  return isMobileDevice ? mobileColumns : columns;
};
